import { useEffect, useRef } from 'react';

/** How often to re-check work that a background worker is still doing. */
export const POLL_INTERVAL_MS = 2000;

/**
 * Re-run `callback` on an interval for as long as `active` is true.
 *
 * Ingestion happens in a Celery worker, so the page has to ask again to learn
 * that a dataset became ready. The timer is cleared as soon as the work
 * settles, and on unmount — a page left open must not poll forever.
 */
export function usePollWhile(
  active: boolean,
  callback: () => void | Promise<void>,
  intervalMs: number = POLL_INTERVAL_MS,
): void {
  // Keeping the latest callback in a ref means a changing closure does not
  // restart the timer on every render.
  const latest = useRef(callback);
  latest.current = callback;

  useEffect(() => {
    if (!active) {
      return;
    }

    const timer = window.setInterval(() => void latest.current(), intervalMs);
    return () => window.clearInterval(timer);
  }, [active, intervalMs]);
}
